// src/pages/Home.jsx
import React, { useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useInView } from "../Hooks/useGlowEffect";
import QuoteBlock from "../components/QuoteBlock";

const ImageCard = ({ title, text, cta, to, imgSrc }) => {
  return (
    <div
      className="
        group mm-card overflow-hidden p-0
        flex flex-col h-full
        border border-mmBorder
        transition-all duration-300
        hover:-translate-y-[2px]
        hover:border-mmAccent/40
        hover:shadow-[0_0_0_1px_rgba(31,111,235,0.14),0_10px_24px_-18px_rgba(0,0,0,0.35)]
      "
    >
      {/* Image header */}
      <div className="relative h-[180px] md:h-[220px] w-full overflow-hidden">
        <img
          src={imgSrc}
          alt={title}
          className="
            absolute inset-0 h-full w-full object-cover
            transition-transform duration-500 ease-out
            group-hover:scale-[1.06]
          "
          width={800}
          height={533}
          loading="lazy"
          decoding="async"
        />

        {/* subtle gradient overlay (geen titel op de foto) */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent opacity-90 transition-opacity duration-300" />
      </div>

      {/* Content (flex column zodat button altijd onderaan blijft) */}
      <div className="p-6 md:p-8 flex flex-col flex-1">
        <h3 className="font-display uppercase tracking-widest text-[18px] md:text-[22px] text-mmText mb-3">
          {title}
        </h3>

        <p className="text-mmTextMuted text-[15px] md:text-[16px] leading-relaxed">
          {text}
        </p>

        {cta && to && (
          <div className="mt-auto pt-6">
            <Link to={to}>
              <button className="mm-btn px-6">
                <span>{cta}</span>
                <span className="shrink-0">→</span>
              </button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const StepCard = ({ step, title, text }) => {
  return (
    <div className="mm-card p-6 md:p-8">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 rounded-full border border-mmBorderStrong flex items-center justify-center text-mmAccent font-semibold">
          {step}
        </div>
        <h3 className="font-display uppercase tracking-widest text-mmText text-sm md:text-base">
          {title}
        </h3>
      </div>
      <p className="text-mmTextMuted text-sm md:text-base leading-relaxed">
        {text}
      </p>
    </div>
  );
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Mission Movement",
  url: "https://missionmovement.nl/",
  logo: "https://missionmovement.nl/img/mmvmtlogo.png",
  founder: {
    "@type": "Person",
    name: "Justin Peeters",
  },
  sameAs: [
    "https://www.instagram.com/mission.movement",
    "https://www.linkedin.com/company/117153940/",
  ],
  description:
    "Mission Movement is een Nederlands voorbereidingsplatform voor kandidaten richting Defensie, politie, brandweer, KMar en specialistische eenheden.",
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Mission Movement",
  url: "https://missionmovement.nl/",
  inLanguage: "nl-NL",
  description:
    "Mission Movement helpt kandidaten zich fysiek en mentaal voorbereiden op Defensie en veiligheidsdiensten.",
};

const Home = ({ animateOnMount = true }) => {
  const { ref, isVisible } = useInView();
  const programmasRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const tracks = [
    {
      title: "Defensie",
      items: [
        "Koninklijke Landmacht, Luchtmacht, Marine",
        "Korps Mariniers",
        "Korps Commando Troepen",
        "11 Luchtmobiele Brigade",
      ],
    },
    {
      title: "Veiligheidsdiensten",
      items: ["Politie", "Marechaussee", "Brandweer"],
    },
  ];

  const coreValues = [
    "Discipline",
    "Broederschap",
    "Doelgerichtheid",
    "Eer",
    "Structuur",
  ];

  const clearProgrammasHash = React.useCallback(() => {
    const hash = window.location.hash || location.hash;

    if (hash !== "#programmas") return;

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: "",
      },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  React.useEffect(() => {
    clearProgrammasHash();
  }, [clearProgrammasHash]);

  const scrollToProgrammas = () => {
    if (!programmasRef.current) return;

    programmasRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    clearProgrammasHash();

    window.setTimeout(clearProgrammasHash, 250);
    window.setTimeout(clearProgrammasHash, 700);
  };

  return (
    <div className="bg-mmPage">
      <Helmet>
        <title>
          Mission Movement | Militaire Voorbereiding voor Defensie &
          Veiligheidsdiensten
        </title>
        <meta
          name="description"
          content="Mission Movement helpt kandidaten zich fysiek en mentaal voorbereiden op Defensie, politie, brandweer, KMar en specialistische eenheden."
        />
        <meta
          property="og:title"
          content="Mission Movement | Militaire Voorbereiding voor Defensie & Veiligheidsdiensten"
        />
        <meta
          property="og:description"
          content="Mission Movement helpt kandidaten zich fysiek en mentaal voorbereiden op Defensie, politie, brandweer, KMar en specialistische eenheden."
        />
        <meta
          property="og:image"
          content="https://missionmovement.nl/og-image.jpg"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://missionmovement.nl/" />
        <meta
          name="twitter:image"
          content="https://missionmovement.nl/og-image.jpg"
        />
        <link rel="canonical" href="https://missionmovement.nl/" />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      </Helmet>

      {/* HERO */}
      <header className="relative w-full h-[100svh] min-h-[620px] md:h-[640px] md:min-h-0 overflow-hidden flex items-center justify-center">
        <img
          src="/img/royalmarines.webp"
          alt="Training onder druk"
          className="absolute inset-0 w-full h-full object-cover object-[42%_center] md:object-bottom scale-[1.16] md:scale-100 transition-transform duration-300"
          width={858}
          height={536}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        <div className="absolute inset-0 mm-heroOverlay" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/70 md:from-black/35 md:via-black/10 md:to-black/65" />

        <div className="relative z-10 w-full max-w-4xl flex flex-col items-center text-center px-6 sm:px-8 lg:px-0">
          <p className="mb-5 rounded-full border border-white/10 bg-black/20 px-3.5 py-1.5 font-display uppercase tracking-[0.28em] text-[11px] font-medium text-white/78 backdrop-blur-[1px] md:mb-5 md:text-xs">
            Defensie <span className="text-mmAccent/70">|</span>{" "}
            Veiligheidsdiensten
          </p>

          <h1 className="h1-teko max-w-[340px] text-[43px] leading-[0.95] tracking-[0.055em] text-white sm:max-w-[440px] sm:text-[54px] md:w-[min(92vw,980px)] md:max-w-none md:text-[clamp(58px,7.1vw,78px)] md:leading-[0.9] md:tracking-[0.035em] xl:text-[82px]">
            <span className="block md:hidden">Bereid je voor</span>
            <span className="block md:hidden">op wat straks echt</span>
            <span className="block md:hidden">gevraagd wordt.</span>
            <span className="hidden whitespace-nowrap md:block">
              Bereid je voor op wat
            </span>
            <span className="hidden whitespace-nowrap md:block">
              straks echt gevraagd wordt.
            </span>
          </h1>

          <div className="mt-5 h-[2px] w-16 rounded-full bg-mmAccent/90 md:mt-6 md:w-20" />

          <p className="mt-5 max-w-[560px] text-[16px] md:text-[18px] leading-relaxed text-white/84">
            <span className="md:hidden">
              Een gerichte opbouw richting selectie, opleiding en het werk
              daarna.
            </span>
            <span className="hidden md:block">
              Een gerichte opbouw richting selectie,
              <br />
              opleiding en het werk daarna.
            </span>
          </p>

          <div className="mt-7 flex w-full max-w-[300px] flex-col items-center gap-3 md:mt-8 md:max-w-none md:flex-row md:justify-center md:gap-4">
            <Link
              to="/voorbereiding-check"
              className="inline-flex min-h-[46px] w-full items-center justify-center rounded-lg border border-mmAccent bg-mmAccent px-5 py-2.5 text-[15px] font-bold leading-tight text-black transition-all hover:bg-transparent hover:text-mmAccent md:w-auto md:min-w-[210px] md:px-6 md:text-base"
            >
              Doe de gratis check
            </Link>
            <button
              type="button"
              onClick={scrollToProgrammas}
              className="inline-flex min-h-[46px] w-full items-center justify-center rounded-lg border border-white/25 bg-white/[0.03] px-5 py-2.5 text-[15px] font-bold leading-tight text-white/90 transition-all hover:border-mmAccent/70 hover:bg-black/20 hover:text-mmAccent md:w-auto md:min-w-[190px] md:px-6 md:text-base"
            >
              Bekijk programma’s
            </button>
          </div>
        </div>
      </header>

      <section className="bg-mmPage px-5 sm:px-6 md:px-10 lg:px-20 py-6">
        <div className="mm-container">
          <div className="rounded-2xl border border-white/15 bg-mmSurface px-5 py-5 shadow-lg shadow-black/20 md:px-7 md:py-6">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div className="max-w-3xl text-center md:text-left">
                <p className="font-display uppercase tracking-[0.2em] text-mmAccent text-sm md:text-base">
                  Nieuw traject
                </p>
                <h2 className="mt-2 font-display uppercase tracking-widest text-mmText text-xl md:text-2xl">
                  Cluster 6 Voorbereiding voor Vrouwen
                </h2>
                <p className="mt-2 text-sm md:text-base text-mmTextMuted leading-relaxed">
                  Na een sterke start met de eerste vrouwen binnen Mission
                  Movement open ik een gerichte begeleide route voor vrouwen
                  richting Cluster 6, Mariniers, KCT of andere fysiek zware
                  Defensierichtingen.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row md:justify-end">
                <Link
                  to="/cluster-6-voorbereiding-vrouwen"
                  className="mm-btnPrimary inline-flex justify-center"
                >
                  Bekijk het begeleide traject
                </Link>
                <Link
                  to="/contact#contact-form"
                  className="mm-btn inline-flex justify-center"
                >
                  Vraag informatie aan
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-mmPage px-5 sm:px-6 md:px-10 lg:px-20 pb-6">
        <div className="mm-container">
          <div className="rounded-2xl border border-white/15 bg-mmSurface px-5 py-5 shadow-lg shadow-black/20 md:px-7 md:py-6">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div className="max-w-3xl text-center md:text-left">
                <h2 className="font-display uppercase tracking-widest text-mmAccent text-xl md:text-2xl">
                  Weet je waar je staat?
                </h2>
                <p className="mt-2 text-sm md:text-base text-mmTextMuted leading-relaxed">
                  Doe de gratis voorbereidings-check en ontdek waar jouw
                  grootste aandachtspunten liggen richting Defensie of
                  veiligheidsdiensten.
                </p>
              </div>
              <Link
                to="/voorbereiding-check"
                className="mm-btnPrimary inline-flex w-full justify-center shrink-0 sm:w-auto"
              >
                Doe de gratis check
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* VOOR WIE */}
      <section className="bg-mmPage py-14 md:py-16 px-6 md:px-10 lg:px-20">
        <div className="mm-container">
          <p className="text-mmTextMuted text-center text-sm md:text-base leading-relaxed max-w-3xl mx-auto mb-8">
            Mission Movement is een Nederlands voorbereidingsplatform voor
            kandidaten richting Defensie, politie, brandweer, KMar en
            specialistische eenheden.
          </p>

          <h2 className="mm-h2 text-center mb-8">Voor wie is dit?</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tracks.map((track) => (
              <div
                key={track.title}
                className="mm-card p-6 md:p-8 flex flex-col h-full"
              >
                <div className="flex-1">
                  <h3 className="text-mmAccent font-display uppercase tracking-widest text-sm md:text-base mb-3">
                    {track.title}
                  </h3>
                  <ul className="text-mmTextMuted text-sm md:text-base space-y-2">
                    {track.items.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="text-mmAccent mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Optional CTA per card */}
                <div className="mt-auto pt-6">
                  <Link
                    to={
                      track.title === "Defensie"
                        ? "/program"
                        : "/programma-veiligheidsdiensten"
                    }
                  >
                    <span className="text-mmAccent font-semibold hover:opacity-80 transition">
                      Bekijk {track.title.toLowerCase()} →
                    </span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE VALUES BAR */}
      <section className="bg-mmSurface py-4 border-t border-b border-mmBorder w-full text-center">
        <div className="text-mmTextMuted font-display tracking-widest uppercase text-md md:text-lg flex flex-wrap justify-center gap-4 px-4">
          {coreValues.map((value, index) => (
            <React.Fragment key={value}>
              <span className="px-1 hover:text-mmAccent transition duration-200">
                {value}
              </span>
              {index < coreValues.length - 1 && (
                <span className="text-mmAccent">|</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* HOE HET WERKT (screenshot 1 vibe) */}
      <section className="bg-mmPage py-16 md:py-20 px-6 md:px-10 lg:px-20">
        <div className="mm-container">
          <h2 className="mm-h2 text-center mb-10">Hoe het werkt</h2>

          <p className="text-mmTextMuted text-center text-sm md:text-base leading-relaxed max-w-3xl mx-auto mb-8">
            Mission Movement helpt je bouwen aan kracht, conditie,
            belastbaarheid, herstel en mentale weerbaarheid vanuit jouw
            startniveau en doelrichting.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StepCard
              step="1"
              title="Startniveau"
              text="Je voorbereiding begint bij waar je nu staat: je kracht, conditie, mobiliteit, herstel en trainingsachtergrond."
            />
            <StepCard
              step="2"
              title="Doelrichting"
              text="De opbouw sluit aan op de richting waar je naartoe werkt: Defensie, politie, brandweer, KMar of een specifieke eenheid."
            />
            <StepCard
              step="3"
              title="Belastbaarheid"
              text="Je bouwt aan het vermogen om training, vermoeidheid, volume en herhaling beter te verdragen."
            />
            <StepCard
              step="4"
              title="Progressie"
              text="Elke training heeft een functie binnen de grotere opbouw, met aandacht voor herstel en uitvoering."
            />
            <StepCard
              step="5"
              title="Bijsturing"
              text="Bij persoonlijke begeleiding sturen we bij op basis van voortgang, herstel, uitvoering en praktische vragen."
            />
          </div>

          <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-6">
            <button
              type="button"
              onClick={scrollToProgrammas}
              className="mm-btnPrimary px-8"
            >
              Kies je route
            </button>
          </div>
        </div>
      </section>

      {/* WAT JE KRIJGT */}
      <section className="w-full bg-mmPage py-16 md:py-20 px-6 md:px-10 lg:px-20 flex flex-col items-center">
        <h2 className="mm-h2 text-center mb-12 border-b border-mmBorder pb-4 w-full max-w-4xl">
          Wat je krijgt
        </h2>
        <div className="grid md:grid-cols-2 gap-10 w-full max-w-5xl text-mmTextMuted font-normal text-sm md:text-base leading-relaxed">
          <div className="mm-card space-y-4 p-6">
            <h3 className="text-mmAccent font-display uppercase tracking-widest mb-2 text-sm md:text-base">
              Dit zit erin:
            </h3>
            <ul className="list-none space-y-2">
              <li className="flex gap-2 items-start">
                <span className="text-mmAccent mt-1">•</span>
                Weekschema met kracht, conditie, mobiliteit en mentale
                weerbaarheid
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-mmAccent mt-1">•</span>
                Watervertrouwen & zwemopbouw (waar relevant)
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-mmAccent mt-1">•</span>
                Videobibliotheek voor techniek en uitvoering
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-mmAccent mt-1">•</span>
                Tracking & voortgangsoverzicht voor duidelijke keuzes
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-mmAccent mt-1">•</span>
                Check-ins & structuur voor consistentie
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-mmAccent mt-1">•</span>
                Praktijkgedreven: gebouwd op echte eisen, niet trends
              </li>
            </ul>
          </div>

          <div className="mm-card space-y-4 p-6">
            <h3 className="text-mmAccent font-display uppercase tracking-widest mb-2 text-sm md:text-base">
              Wat je eraan overhoudt:
            </h3>
            <ul className="list-none space-y-2">
              <li className="flex gap-2 items-start">
                <span className="text-mmAccent mt-1">•</span>
                Betere belastbaarheid en minder blessure-ruis
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-mmAccent mt-1">•</span>
                Sterkere mindset door structuur, druk en discipline
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-mmAccent mt-1">•</span>
                Vertrouwen omdat je exact weet wat je doet (en waarom)
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-mmAccent mt-1">•</span>
                Een fitter lichaam dat presteert onder vermoeidheid
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-mmAccent mt-1">•</span>
                Helderheid: waar je staat, wat je mist, wat je volgende stap is
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-mmAccent mt-1">•</span>
                Meer rust in je voorbereiding: je volgt een bewezen opbouw
              </li>
            </ul>
          </div>
        </div>
        {/* Subtle intake CTA (vervangt de Programma CTA) */}
        <div className="mt-12 w-full flex flex-col items-center text-center">
          <Link to="/contact#contact-form" className="mm-btn">
            Plan eerste contact
          </Link>

          <p className="text-mmTextMuted text-sm md:text-base mt-2 max-w-xl">
            Korte call om jouw route en startniveau scherp te krijgen.
          </p>
        </div>
      </section>

      {/* IMAGE CARDS (Optie A) */}
      <section className="bg-mmPage py-16 md:py-20 px-6 md:px-10 lg:px-20">
        <div className="mm-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ImageCard
              title="Over Mission Movement"
              imgSrc="/img/royalmarines.webp"
              text="Mission Movement is ontstaan uit jaren ervaring binnen Defensie, coaching en fysieke voorbereiding. Het programma brengt die ervaring samen in een duidelijke 12-weekse aanpak voor kandidaten die serieus richting Defensie of veiligheidsdiensten werken. Je traint met structuur, bouwt belastbaarheid op en leert wat er nodig is om voorbereid aan je traject te beginnen."
              cta="Lees meer"
              to="/about"
            />

            <ImageCard
              title="Zij die het verdiend hebben"
              imgSrc="/img/eenheden/veiligheidsdiensten.jpg"
              text="Elke kandidaat start met een eigen doel en startniveau. In de ervaringen lees je hoe begeleiding, structuur en duidelijke opbouw hebben geholpen om consistenter te trainen, sterker te worden en met meer vertrouwen richting selectie, opleiding of functie-eisen te werken."
              cta="Bekijk ervaringen"
              to="/ervaringen"
            />

            <div
              ref={programmasRef}
              className="md:col-span-2 scroll-mt-24 md:scroll-mt-28 lg:scroll-mt-32"
            >
              <div className="group mm-card overflow-hidden p-0 border border-mmBorder transition-all duration-300 hover:-translate-y-[2px] hover:border-mmAccent/40">
                <div className="relative h-[280px] md:h-[300px] w-full overflow-hidden">
                  <img
                    src="/img/programmas.png"
                    alt="Programma's"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
                    width={2560}
                    height={1707}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent opacity-90 transition-opacity duration-300" />
                </div>

                <div className="p-6 md:p-8">
                  <h3 className="font-display uppercase tracking-widest text-[18px] md:text-[22px] text-mmText mb-3">
                    Programma&apos;s
                  </h3>
                  <p className="text-mmTextMuted text-[15px] md:text-[16px] leading-relaxed max-w-4xl">
                    Kies de 12-weekse voorbereiding die past bij jouw doel. De
                    programma’s zijn opgebouwd rond oefenselectie, progressieve
                    belasting, intensiteitsbewaking en herstel. Elke
                    trainingsweek heeft een functie: van technische basis en
                    belastbaarheid tot taakgerichte capaciteit en testmomenten.
                    Zo leer je niet alleen trainen voor selectie of keuring,
                    maar bouw je handvatten op die je later in opleiding en
                    loopbaan blijft gebruiken.
                  </p>

                  <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
                    <Link to="/program">
                      <button className="mm-btnPrimary px-6">
                        <span>Defensie-programma</span>
                        <span className="shrink-0">→</span>
                      </button>
                    </Link>
                    <Link to="/programma-veiligheidsdiensten">
                      <button className="mm-btnPrimary px-6">
                        <span>Veiligheidsdiensten-programma</span>
                        <span className="shrink-0">→</span>
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <QuoteBlock
        quote="Je stijgt niet tot het niveau van je doelen. Je zakt tot het niveau van je training."
        author="Mission Movement"
      />

      {/* FINAL CTA */}
      <section className="h-[360px] md:h-[600px] w-full flex justify-center items-center px-6 md:px-10 lg:px-20 bg-mmPage border-t border-mmBorder">
        <div className="w-full flex flex-col justify-center items-center max-w-5xl mx-auto">
          <h2
            ref={ref}
            className={`mm-h1 text-mmText text-[34px] md:text-[70px] mb-3 text-center transition-all duration-700 ease-in-out ${
              !animateOnMount || isVisible
                ? "translate-x-0 opacity-100"
                : "-translate-x-10 opacity-0"
            }`}
          >
            Dit is het pad. <br></br>Jij kiest of je het bewandelt.
          </h2>

          <p className="text-mmTextMuted tracking-wider md:text-[18px] text-center leading-relaxed max-w-3xl">
            Dit is voorbereiding op echte eisen: <br></br>structuur,
            belastbaarheid en presteren onder druk. <br />
            Weinigen kiezen dit. Nog minder maken het af.
          </p>

          <Link to="/pricing">
            <button className="mm-btnPrimary my-7 md:my-10">
              Start je voorbereiding
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
